import moment from "moment";
import React, { ReactNode, useEffect, useRef, useState } from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import Divider from "veneer/Divider";
import FormControlLabel from "veneer/FormControlLabel";
import { IconButton, LinkIconButton } from "veneer/IconButton";
import InitialsAvatar from "veneer/InitialsAvatar";
import MarkdownViewer from "veneer/MarkdownViewer";
import { MultiTextInput } from "veneer/MultiTextInput";
import Stack from "veneer/Stack";
import {
  CheckFilledIcon,
  FormatBoldIcon,
  FormatItalicIcon,
  UpdateIcon,
} from "veneer/SvgIcon";
import Switch from "veneer/Switch";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import type { IncidentComment } from "cortex/ui/src/fleet/service/incidents";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";
import type { User } from "cortex/ui/src/user/model";

import IncidentStatus, {
  type IncidentStatusValue,
  getStatusFromIncidentStatusChangeComment,
} from "./IncidentStatus";

interface IncidentCommentsProps {
  canComment: boolean;
  canResolve: boolean;
  comments: ReadonlyArray<IncidentComment>;
  error: string;
  loading: boolean;
  markResolved: boolean;
  newComment: string;
  onCommentChange: (comment: string) => void;
  onMarkResolvedChange: (markResolved: boolean) => void;
  onSubmit: () => void;
  resolution?: IncidentResolution;
  submitting: boolean;
  users: Record<string, User>;
}

interface CommentListProps {
  comments: ReadonlyArray<IncidentComment>;
  error: string;
  loading: boolean;
  resolution?: IncidentResolution;
  users: Record<string, User>;
}

interface IncidentCommentEditorProps {
  canResolve: boolean;
  markResolved: boolean;
  newComment: string;
  onCommentChange: (comment: string) => void;
  onMarkResolvedChange: (markResolved: boolean) => void;
  onSubmit: () => void;
  showSubmitButton?: boolean;
  submitting: boolean;
}

interface IncidentResolution {
  comment?: string;
  date?: string;
  userId?: number | null;
}

interface TimelineItem {
  action: "changed status on" | "commented on" | "resolved on";
  comment: string;
  date?: string;
  id: string;
  kind: "comment" | "resolution" | "status";
  order: number;
  status?: IncidentStatusValue;
  userId?: number | null;
}

type EditorMode = "preview" | "write";

function parseTimelineDate(date?: string) {
  if (!date) {
    return null;
  }

  const parsedDate = moment.utc(date);
  return parsedDate.isValid() ? parsedDate : null;
}

export default function IncidentComments({
  canComment,
  canResolve,
  comments,
  error,
  loading,
  markResolved,
  newComment,
  onCommentChange,
  onMarkResolvedChange,
  onSubmit,
  resolution,
  submitting,
  users,
}: IncidentCommentsProps) {
  return (
    <>
      <CommentTile title="Comments">
        <CommentList
          comments={comments}
          error={error}
          loading={loading}
          resolution={resolution}
          users={users}
        />
      </CommentTile>
      {canComment && (
        <CommentTile title="Add Comment">
          <IncidentCommentEditor
            canResolve={canResolve}
            markResolved={markResolved}
            newComment={newComment}
            onCommentChange={onCommentChange}
            onMarkResolvedChange={onMarkResolvedChange}
            onSubmit={onSubmit}
            submitting={submitting}
          />
        </CommentTile>
      )}
    </>
  );
}

export function IncidentCommentEditor({
  canResolve,
  markResolved,
  newComment,
  onCommentChange,
  onMarkResolvedChange,
  onSubmit,
  showSubmitButton = true,
  submitting,
}: IncidentCommentEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [editorMode, setEditorMode] = useState<EditorMode>("write");
  const trimmedComment = newComment.trim();

  useEffect(() => {
    if (!newComment) {
      setEditorMode("write");
    }
  }, [newComment]);

  const applyMarkdown = (format: "bold" | "italic" | "link") => {
    const textarea = editorRef.current?.querySelector("textarea");

    if (!textarea) {
      return;
    }

    const value = textarea.value;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const selectedText = value.slice(selectionStart, selectionEnd);
    let replacement = "";
    let nextSelectionStart = selectionStart;
    let nextSelectionEnd = selectionEnd;

    if (format === "bold") {
      const text = selectedText || "bold text";
      replacement = `**${text}**`;
      nextSelectionStart += 2;
      nextSelectionEnd = nextSelectionStart + text.length;
    } else if (format === "italic") {
      const text = selectedText || "italic text";
      replacement = `*${text}*`;
      nextSelectionStart += 1;
      nextSelectionEnd = nextSelectionStart + text.length;
    } else {
      const text = selectedText || "link text";
      replacement = `[${text}](url)`;
      nextSelectionStart += text.length + 3;
      nextSelectionEnd = nextSelectionStart + 3;
    }

    onCommentChange(
      `${value.slice(0, selectionStart)}${replacement}${value.slice(selectionEnd)}`,
    );

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };

  return (
    <Stack alignItems="flex-start" gap={2}>
      <EditorShell>
        <EditorHeader>
          <EditorTabs>
            <EditorTab
              aria-selected={editorMode === "write"}
              data-testid="incident-comment-write-tab"
              isActive={editorMode === "write"}
              onClick={() => setEditorMode("write")}
              type="button"
            >
              Write
            </EditorTab>
            <EditorTab
              aria-selected={editorMode === "preview"}
              data-testid="incident-comment-preview-tab"
              isActive={editorMode === "preview"}
              onClick={() => setEditorMode("preview")}
              type="button"
            >
              Preview
            </EditorTab>
          </EditorTabs>
          <EditorActions>
            {editorMode === "write" && (
              <Stack direction="row" gap={0.5}>
                <LinkIconButton
                  aria-label="Add link"
                  dataTestId="incident-comment-link"
                  iconSize="small"
                  onClick={() => applyMarkdown("link")}
                  size="small"
                  title="Add link"
                />
                <IconButton
                  aria-label="Bold"
                  dataTestId="incident-comment-bold"
                  icon={<FormatBoldIcon size="xsmall" />}
                  onClick={() => applyMarkdown("bold")}
                  size="small"
                  title="Bold"
                />
                <IconButton
                  aria-label="Italic"
                  dataTestId="incident-comment-italic"
                  icon={<FormatItalicIcon size="xsmall" />}
                  onClick={() => applyMarkdown("italic")}
                  size="small"
                  title="Italic"
                />
              </Stack>
            )}
            {canResolve && (
              <FormControlLabel
                control={
                  <Switch
                    checked={markResolved}
                    onChange={() => {
                      onMarkResolvedChange(!markResolved);
                    }}
                  />
                }
                disabled={submitting}
                label="Mark as resolved"
              />
            )}
          </EditorActions>
        </EditorHeader>
        <EditorPanel>
          {editorMode === "write" ? (
            <Box ref={editorRef} width="100%">
              <MultiTextInput
                autoFocus
                dataTestId="incident-comment-input"
                fullWidth
                helperText="Markdown shortcuts for bold, italics, and links are supported."
                label="Comment"
                minRows={4}
                onChange={(newValue: string | null) =>
                  onCommentChange(newValue ?? "")
                }
                placeholder="Investigating **network health** with the *operations* team. [Runbook](https://example.com)"
                value={newComment}
              />
            </Box>
          ) : (
            <CommentPreview data-testid="incident-comment-preview">
              {trimmedComment ? (
                <MarkdownViewer markdown={newComment} />
              ) : (
                <Typography color={Colors.black56} variant="body2">
                  Nothing to preview.
                </Typography>
              )}
            </CommentPreview>
          )}
        </EditorPanel>
      </EditorShell>
      {showSubmitButton && (
        <Button
          dataTestId="incident-comment-submit"
          disabled={!trimmedComment || submitting}
          loading={submitting}
          onClick={onSubmit}
          variant="contained"
        >
          {markResolved ? "Resolve" : "Comment"}
        </Button>
      )}
    </Stack>
  );
}

function CommentList({
  comments,
  error,
  loading,
  resolution,
  users,
}: CommentListProps) {
  const timelineItems: ReadonlyArray<TimelineItem> = [
    ...comments.map((comment, index) => {
      const status = getStatusFromIncidentStatusChangeComment(comment.comment);

      return {
        action: status
          ? ("changed status on" as const)
          : ("commented on" as const),
        comment: comment.comment,
        date: comment.create_date,
        id: `comment-${comment.id}`,
        kind: status ? ("status" as const) : ("comment" as const),
        order: index,
        status,
        userId: comment.user_id,
      };
    }),
    ...(resolution
      ? [
          {
            action: "resolved on" as const,
            comment: resolution.comment ?? "",
            date: resolution.date,
            id: "resolution",
            kind: "resolution" as const,
            order: comments.length,
            userId: resolution.userId,
          },
        ]
      : []),
  ].sort((left, right) => {
    const leftTime = parseTimelineDate(left.date)?.valueOf() ?? Infinity;
    const rightTime = parseTimelineDate(right.date)?.valueOf() ?? Infinity;

    return leftTime - rightTime || left.order - right.order;
  });

  if (loading) {
    return (
      <Typography dataTestId="incident-comments-loading" variant="body2">
        Loading comments...
      </Typography>
    );
  }

  if (!error && timelineItems.length === 0) {
    return (
      <Typography
        color={Colors.black56}
        dataTestId="incident-comments-empty"
        variant="body2"
      >
        No comments yet.
      </Typography>
    );
  }

  return (
    <Stack gap={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack dataTestId="incident-comments" gap={0}>
        {timelineItems.map((item, index) => {
          const user =
            item.userId != null ? users[String(item.userId)] : undefined;
          const userName = user?.full_name || user?.name || "Unknown user";
          const isLast = index === timelineItems.length - 1;
          const isResolution = item.kind === "resolution";
          const formattedDate = parseTimelineDate(item.date)
            ? formatDateTimeLocal(item.date ?? "")
            : "date unavailable";

          if (isResolution) {
            return (
              <Box
                dataTestId="incident-resolution-timeline-item"
                display="flex"
                gap={2}
                key={item.id}
                minWidth={0}
              >
                <Box
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                  flexShrink={0}
                  width={36}
                >
                  <ResolutionEventIcon>
                    <CheckFilledIcon
                      htmlColor={Colors.green700}
                      size="xsmall"
                    />
                  </ResolutionEventIcon>
                  {!isLast && (
                    <Box
                      bgcolor={Colors.gray300}
                      flex={1}
                      minHeight={24}
                      width="2px"
                    />
                  )}
                </Box>
                <Box flex={1} minWidth={0} pb={isLast ? 0 : 3} pt={0.5}>
                  <Stack
                    alignItems="center"
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <InitialsAvatar fullName={userName} size="small" />
                    <Typography
                      color={Colors.gray1000}
                      fontWeight="medium"
                      variant="body2"
                    >
                      {userName}
                    </Typography>
                    <Typography color={Colors.black56} variant="body2">
                      resolved this incident
                    </Typography>
                    <Typography color={Colors.black56} variant="caption">
                      {formattedDate}
                    </Typography>
                  </Stack>
                  {item.comment && (
                    <ResolutionNote>
                      <MarkdownViewer markdown={item.comment} />
                    </ResolutionNote>
                  )}
                </Box>
              </Box>
            );
          }

          if (item.status) {
            const colors = getTimelineColors({
              isResolution: false,
              status: item.status,
            });

            return (
              <Box
                dataTestId="incident-status-timeline-item"
                display="flex"
                gap={2}
                key={item.id}
                minWidth={0}
              >
                <Box
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                  flexShrink={0}
                  width={36}
                >
                  <StatusEventIcon status={item.status}>
                    <UpdateIcon htmlColor={colors.border} size="xsmall" />
                  </StatusEventIcon>
                  {!isLast && (
                    <Box
                      bgcolor={Colors.gray300}
                      flex={1}
                      minHeight={24}
                      width="2px"
                    />
                  )}
                </Box>
                <Stack
                  alignItems="center"
                  direction="row"
                  flex={1}
                  flexWrap="wrap"
                  gap={1}
                  minWidth={0}
                  pb={isLast ? 0 : 3}
                  pt={0.5}
                >
                  <InitialsAvatar fullName={userName} size="small" />
                  <Typography
                    color={Colors.gray1000}
                    fontWeight="medium"
                    variant="body2"
                  >
                    {userName}
                  </Typography>
                  <Typography color={Colors.black56} variant="body2">
                    changed status to
                  </Typography>
                  <IncidentStatus status={item.status} />
                  <Typography color={Colors.black56} variant="caption">
                    {formattedDate}
                  </Typography>
                </Stack>
              </Box>
            );
          }

          return (
            <Box display="flex" gap={2} key={item.id} minWidth={0}>
              <Box
                alignItems="center"
                display="flex"
                flexDirection="column"
                flexShrink={0}
                width={36}
              >
                <Box flexShrink={0} position="relative" zIndex={1}>
                  <InitialsAvatar fullName={userName} size="large" />
                </Box>
                {!isLast && (
                  <Box
                    bgcolor={Colors.gray300}
                    flex={1}
                    minHeight={24}
                    width="2px"
                  />
                )}
              </Box>
              <Box flex={1} minWidth={0} pb={isLast ? 0 : 3}>
                <CommentWrapper
                  borderRadius={2}
                  isResolution={isResolution}
                  position="relative"
                  status={item.status}
                >
                  <CommentArrow
                    isResolution={isResolution}
                    status={item.status}
                  />
                  <CommentHeader
                    borderRadius="4px 4px 0 0"
                    isResolution={isResolution}
                    px={2}
                    py={1.5}
                    status={item.status}
                  >
                    <Stack
                      alignItems="center"
                      direction="row"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Typography
                        color={Colors.gray1000}
                        fontWeight="medium"
                        variant="body2"
                      >
                        {userName}
                      </Typography>
                      <Typography color={Colors.black56} variant="caption">
                        {item.action} {formattedDate}
                      </Typography>
                    </Stack>
                  </CommentHeader>
                  <CommentDivider
                    isResolution={isResolution}
                    status={item.status}
                  />
                  <CommentBody isResolution={isResolution} status={item.status}>
                    {item.comment ? (
                      <MarkdownViewer markdown={item.comment} />
                    ) : (
                      <Typography color={Colors.black56} variant="body2">
                        No resolution note.
                      </Typography>
                    )}
                  </CommentBody>
                </CommentWrapper>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}

const CommentTile = ({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) => (
  <Stack gap={2} p={2}>
    <Typography color={Colors.gray1000} variant="h4">
      {title}
    </Typography>
    {children}
  </Stack>
);

const EditorShell = styled(Box)(({ theme }) => ({
  border: `1px solid ${Colors.gray300}`,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  width: "100%",
}));

const EditorHeader = styled(Box)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: Colors.gray100,
  borderBottom: `1px solid ${Colors.gray300}`,
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
}));

const EditorTabs = styled(Box)({
  alignSelf: "stretch",
  display: "flex",
});

interface EditorTabProps {
  isActive: boolean;
}

const EditorTab = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<EditorTabProps>(({ isActive, theme }) => ({
  backgroundColor: isActive ? Colors.white : "transparent",
  border: "1px solid transparent",
  borderBottomColor: isActive ? Colors.white : "transparent",
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  color: isActive ? Colors.gray1000 : Colors.black56,
  cursor: "pointer",
  font: "inherit",
  fontWeight: isActive ? 500 : 400,
  marginBottom: -1,
  padding: theme.spacing(1, 2),

  ...(isActive && {
    borderLeftColor: Colors.gray300,
    borderRightColor: Colors.gray300,
    borderTopColor: Colors.gray300,
  }),
}));

const EditorActions = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  marginLeft: "auto",
}));

const EditorPanel = styled(Box)(({ theme }) => ({
  backgroundColor: Colors.white,
  padding: theme.spacing(2),
}));

const CommentPreview = styled(Box)({
  minHeight: 120,
  overflowWrap: "anywhere",

  "& > :last-child": {
    marginBottom: 0,
  },
});

interface ResolutionStyleProps {
  isResolution: boolean;
  status?: IncidentStatusValue;
}

const shouldForwardTimelineStyleProp = (prop: PropertyKey) =>
  prop !== "isResolution" && prop !== "status";

const getTimelineColors = ({ isResolution, status }: ResolutionStyleProps) => {
  if (status === "new") {
    return { background: Colors.blue100, border: Colors.blue700 };
  }

  if (status === "acknowledged") {
    return { background: Colors.orange100, border: Colors.orange700 };
  }

  if (status === "on_hold") {
    return { background: Colors.red100, border: Colors.red700 };
  }

  if (isResolution) {
    return { background: Colors.green200, border: Colors.green700 };
  }

  return { background: Colors.gray100, border: Colors.gray300 };
};

const getTimelineBodyBackground = (props: ResolutionStyleProps) => {
  if (props.isResolution) {
    return Colors.green100;
  }

  if (props.status) {
    return getTimelineColors(props).background;
  }

  return Colors.white;
};

const StatusEventIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: IncidentStatusValue }>(({ status }) => {
  const colors = getTimelineColors({ isResolution: false, status });
  return {
    alignItems: "center",
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    borderRadius: "50%",
    display: "flex",
    flexShrink: 0,
    height: 28,
    justifyContent: "center",
    position: "relative",
    width: 28,
    zIndex: 1,
  };
});

const ResolutionEventIcon = styled(Box)({
  alignItems: "center",
  backgroundColor: Colors.green200,
  border: `1px solid ${Colors.green700}`,
  borderRadius: "50%",
  display: "flex",
  flexShrink: 0,
  height: 28,
  justifyContent: "center",
  position: "relative",
  width: 28,
  zIndex: 1,
});

const ResolutionNote = styled(Box)(({ theme }) => ({
  backgroundColor: Colors.green100,
  border: `1px solid ${Colors.green300}`,
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(1.5),
  overflowWrap: "anywhere",
  padding: theme.spacing(2),

  "& > :last-child": {
    marginBottom: 0,
  },
}));

const CommentWrapper = styled(Box, {
  shouldForwardProp: shouldForwardTimelineStyleProp,
})<ResolutionStyleProps>((props) => {
  const colors = getTimelineColors(props);
  return {
    border: `1px solid ${colors.border}`,
    overflow: "hidden",
  };
});

const CommentArrow = styled(Box, {
  shouldForwardProp: shouldForwardTimelineStyleProp,
})<ResolutionStyleProps>((props) => {
  const colors = getTimelineColors(props);
  return {
    backgroundColor: colors.background,
    borderBottom: `1px solid ${colors.border}`,
    borderLeft: `1px solid ${colors.border}`,
    height: 12,
    left: -7,
    position: "absolute",
    top: 11,
    transform: "rotate(45deg)",
    width: 12,
    zIndex: 1,
  };
});

const CommentHeader = styled(Box, {
  shouldForwardProp: shouldForwardTimelineStyleProp,
})<ResolutionStyleProps>((props) => ({
  backgroundColor: getTimelineColors(props).background,
}));

const CommentDivider = styled(Divider, {
  shouldForwardProp: shouldForwardTimelineStyleProp,
})<ResolutionStyleProps>((props) => ({
  borderColor: getTimelineColors(props).border,
}));

const CommentBody = styled(Box, {
  shouldForwardProp: shouldForwardTimelineStyleProp,
})<ResolutionStyleProps>((props) => ({
  backgroundColor: getTimelineBodyBackground(props),
  minWidth: 0,
  overflowWrap: "anywhere",
  padding: props.theme.spacing(2),

  "& > :last-child": {
    marginBottom: 0,
  },
}));
